import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import type { ContextType } from 'react';
import { useCarouselDimensions } from '../../../../hooks/useCarouselDimensions';
import { useCarouselHeight } from '../../../../hooks/useCarouselHeight';
import { useCarouselMinHeight } from '../../../../hooks/useCarouselMinHeight';
import { useCompactDesktopWidth, getCompactContainerStyle } from '../../../../hooks/useCompactDesktopWidth';
import { StoreContext } from '../../../../services/store';
import { infiniteScrollTimeout, productListCarouselTransitionDurationSec, partialItemWidth } from '../../../consts';
import { ISettings } from '../../../../types';

interface LayoutParams {
  context: {
    widgetWidth: number;
    contentSize: ISettings['content_size'];
    isMobile: boolean;
    hideArrows: boolean;
  };
  config: {
    contentSize: ISettings['content_size'];
    itemCount: number;
    partialItemWidth: number;
  };
  refs: {
    slideRef: RefObject<HTMLDivElement>;
  };
}

interface HeightParams {
  refs: { outerRef: RefObject<HTMLDivElement> };
  config: {
    thumbnailShape: ISettings['thumbnail_shape'];
    contentSize: ISettings['content_size'];
    itemCount: number;
  };
  layout: {
    isCompact: boolean;
    constrainedWidgetWidth: number;
    widgetWidth: number;
  };
}

type StoreStateUpdater = ContextType<typeof StoreContext>['setStoreState'];

interface NavigationParams {
  config: {
    itemCount: number;
    contentSize: ISettings['content_size'];
    cloneCount: number;
    contentSizeNotReached: boolean;
  };
  refs: {
    slideRef: RefObject<HTMLDivElement>;
    containerRef: RefObject<HTMLDivElement>;
  };
  state: {
    itemWidth: number;
    currentPosition?: number;
  };
  actions: {
    setStoreState: StoreStateUpdater;
  };
}

interface NavigationState {
  activeIndex: number;
  isTransitioning: boolean;
}

// Forces re-render on window resize to update carousel dimensions.
const useForceResizeRerender = (): void => {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => setTick((tick) => tick + 1);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);
};

/** Determines uniform centering config based on item count and visibility.
    If there are fewer items than visible slots, disables partial visibility and cloning.*/
export const useUniformCenteredConfig = (
  itemCount: number,
  contentSize: ISettings['content_size'],
  setStoreState: StoreStateUpdater,
) => {
  const normalizedContentSize = Math.max(contentSize, 1);
  const contentSizeNotReached = itemCount < normalizedContentSize;
  const partialItemWidthValue = contentSizeNotReached ? 0 : partialItemWidth;
  const cloneCount = contentSizeNotReached || itemCount <= 1 ? 0 : Math.min(normalizedContentSize, itemCount);

  useEffect(() => {
    setStoreState((state) => {
      if (state.productListUniformMode === contentSizeNotReached) {
        return state;
      }

      return {
        ...state,
        productListUniformMode: contentSizeNotReached,
      };
    });
  }, [contentSizeNotReached, setStoreState]);

  return { contentSizeNotReached, partialItemWidth: partialItemWidthValue, cloneCount };
};

// Manages layout dimensions, compact mode, and arrow sizing for the carousel.
export const useProductListLayout = ({
  context: { widgetWidth, contentSize, isMobile, hideArrows },
  config: { contentSize: carouselContentSize, itemCount, partialItemWidth },
  refs: { slideRef },
}: LayoutParams) => {
  useForceResizeRerender();

  const { width: constrainedWidgetWidth, isCompact } = useCompactDesktopWidth({
    widgetWidth,
    contentSize,
    isMobile,
  });

  const compactStyle = getCompactContainerStyle(isCompact, constrainedWidgetWidth);
  const carouselDimensions = useCarouselDimensions({
    widgetWidth: constrainedWidgetWidth,
    slideRef,
    hideArrows,
    hasItems: itemCount > 1,
    nbElems: carouselContentSize,
    itemCount,
    isMobile: false,
    partialItemWidth,
    forcedArrowSize: isCompact ? 'medium' : undefined,
  });

  const arrowSize = isCompact ? 'medium' : carouselDimensions.arrowSize;

  return {
    constrainedWidgetWidth,
    isCompact,
    compactStyle,
    carouselDimensions,
    arrowSize,
  };
};

// Calculates enforced minimum height for the carousel container.
export const useProductListHeight = ({
  refs: { outerRef },
  config: { thumbnailShape, contentSize, itemCount },
  layout: { isCompact, constrainedWidgetWidth, widgetWidth },
}: HeightParams) => {
  const carouselHeight = useCarouselHeight({
    ref: outerRef,
    shape: thumbnailShape,
    contentSize,
    itemCount,
    partialItemWidth,
  });
  const enforceConfiguredHeight = contentSize <= 3 && !isCompact;

  const enforcedMinHeight = useCarouselMinHeight(
    outerRef,
    carouselHeight,
    enforceConfiguredHeight,
    `${itemCount}-${isCompact ? constrainedWidgetWidth : widgetWidth}`,
  );

  return { enforcedMinHeight };
};

// Handles navigation state, slide transitions, and position syncing.
export const useProductListNavigation = ({
  config: { itemCount, contentSize, cloneCount, contentSizeNotReached },
  refs: { slideRef, containerRef },
  state: { itemWidth, currentPosition },
  actions: { setStoreState },
}: NavigationParams) => {
  const [navigationState, setNavigationState] = useState<NavigationState>({
    activeIndex: 0,
    isTransitioning: false,
  });

  const { activeIndex, isTransitioning } = navigationState;

  const timeoutRef = useRef<number | null>(null);

  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      if (typeof window !== 'undefined') {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => clearPendingTimeout(), [clearPendingTimeout]);

  const updateStorePosition = useCallback(
    (position: number) => {
      setStoreState((state) => {
        if (state.currentPosition === position) {
          return state;
        }

        return {
          ...state,
          currentPosition: position,
        };
      });
    },
    [setStoreState],
  );

  const getTranslateX = useCallback(
    (index = activeIndex + cloneCount) => {
      if (cloneCount === 0) {
        return 0;
      }

      const containerWidth = slideRef.current?.clientWidth || 0;
      const activePos = index * itemWidth;
      const centerOffset = (containerWidth - itemWidth) / 2;

      return centerOffset - activePos;
    },
    [cloneCount, slideRef, itemWidth, activeIndex],
  );

  const goToSlide = useCallback(
    (index: number, shouldUpdateCurrentPosition = true) => {
      if (itemCount === 0) {
        return;
      }

      if (contentSizeNotReached) {
        const normalizedIndex = ((index % itemCount) + itemCount) % itemCount;
        setNavigationState((state) => ({ ...state, activeIndex: normalizedIndex, isTransitioning: false }));

        if (shouldUpdateCurrentPosition) {
          updateStorePosition(normalizedIndex);
        }
        return;
      }

      setNavigationState((state) => ({ ...state, activeIndex: index, isTransitioning: true }));

      clearPendingTimeout();

      if (typeof window === 'undefined') {
        setNavigationState((state) => ({ ...state, activeIndex: index, isTransitioning: false }));
        if (shouldUpdateCurrentPosition) {
          updateStorePosition(index);
        }
        return;
      }

      timeoutRef.current = window.setTimeout(() => {
        let newIndex = index;
        if (index >= itemCount) newIndex = Math.abs(index) - itemCount;
        if (index < 0) newIndex = itemCount - Math.abs(index);

        if (newIndex !== index && containerRef.current) {
          const container = containerRef.current;
          container.style.transition = 'none';
          container.style.left = `${getTranslateX(newIndex + cloneCount)}px`;

          if (newIndex >= 0 && newIndex < itemCount) {
            if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
              window.requestAnimationFrame(() => {
                if (container) {
                  container.style.transition = `left ${productListCarouselTransitionDurationSec}s ease`;
                }
              });
            }
          }
        }

        setNavigationState((state) => ({ ...state, activeIndex: newIndex, isTransitioning: false }));

        if (shouldUpdateCurrentPosition) {
          updateStorePosition(newIndex);
        }
      }, infiniteScrollTimeout);
    },
    [
      itemCount,
      contentSizeNotReached,
      cloneCount,
      containerRef,
      getTranslateX,
      updateStorePosition,
      clearPendingTimeout,
    ],
  );

  useEffect(() => {
    if (currentPosition === undefined || currentPosition === activeIndex || isTransitioning) {
      return;
    }

    const diff = Math.abs(currentPosition - activeIndex);

    if (diff > contentSize || diff + 1 === itemCount) {
      if (currentPosition > activeIndex) {
        goToSlide(currentPosition - itemCount);
      } else {
        goToSlide(currentPosition + itemCount);
      }
    } else {
      goToSlide(currentPosition);
    }
  }, [currentPosition, activeIndex, contentSize, itemCount, goToSlide, isTransitioning]);

  const handleSlide = useCallback(
    (direction: 'left' | 'right') => {
      if (contentSizeNotReached || isTransitioning) {
        return;
      }

      const delta = direction === 'left' ? -1 : 1;
      goToSlide(activeIndex + delta);
    },
    [contentSizeNotReached, isTransitioning, activeIndex, goToSlide],
  );

  return {
    activeIndex,
    isTransitioning,
    handleSlide,
    getTranslateX,
    goToSlide,
  };
};
